import { useEffect, useRef } from 'react';

export interface UseDragGestureOptions {
    /** Called for every mousemove/touchmove while the gesture is active. Touch moves arrive as a synthetic MouseEvent. */
    onDragMove: (e: MouseEvent) => void;
    /** Called once the gesture ends, after listeners are torn down. */
    onDragEnd?: () => void;
    /**
     * Events that end the gesture automatically. Defaults to mouseup/touchend. Pass `[]` for a gesture
     * that isn't released by letting go of the mouse button (e.g. a click-move-click "draw"
     * interaction) and call the returned `stop` function manually instead.
     */
    endEvents?: Array<'mouseup' | 'touchend'>;
}

const DEFAULT_END_EVENTS: Array<'mouseup' | 'touchend'> = ['mouseup', 'touchend'];

/**
 * Wires up a document-level drag gesture (mousemove/touchmove while active, ending on
 * mouseup/touchend by default) without the usual pitfalls of hand-rolling this. A touchmove has no
 * clientX/clientY of its own, so it's converted to a MouseEvent from `touches[0]` before reaching
 * `onDragMove` -- callers only ever see a MouseEvent. This only covers the move/end half: the
 * gesture still has to be started by the caller (`start()`).
 *
 *  - `onDragMove`/`onDragEnd` are mirrored into a ref every render, so the listeners -- which have
 *    to be added with a *stable* function reference for `removeEventListener` to ever find them
 *    again -- always call the latest version of each callback, not whatever closure happened to
 *    exist when the gesture started. (This is the exact bug class behind a cylinder-warp handle
 *    snapping back to its pre-drag position on release: the frozen handler was committing a value
 *    captured before the drag began.)
 *  - Listeners are torn down on unmount even if a gesture is still in progress (`onDragEnd` is not
 *    called in that case).
 *
 * Because callbacks always see the latest render, a handler that needs the values from when the drag
 * began (e.g. `points`) must snapshot them into a ref in the mousedown handler, not read live props.
 *
 * Returns `{ start, stop }`. Call `start()` from a Konva/DOM `mousedown` handler once you've
 * captured whatever gesture-start snapshot (start position, dragged index, etc.) your own
 * `onDragMove`/`onDragEnd` need -- that bookkeeping is domain-specific per shape and stays in the
 * caller, not in this hook.
 */
export function useDragGesture(options: UseDragGestureOptions) {
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const apiRef = useRef<{ start: () => void; stop: () => void }>();
    if (!apiRef.current) {
        const handleMove = (e: MouseEvent) => {
            optionsRef.current.onDragMove(e);
        };

        const handleTouchMove = (e: TouchEvent) => {
            const { clientX, clientY } = e.touches[0];
            optionsRef.current.onDragMove(new MouseEvent('mousemove', { clientX, clientY }));
        };

        const handleEnd = () => {
            stop();
            optionsRef.current.onDragEnd?.();
        };

        // Remembered from `start` so `stop` removes exactly what was added, even if `endEvents` changed since.
        let activeEndEvents = DEFAULT_END_EVENTS;

        const start = () => {
            activeEndEvents = optionsRef.current.endEvents ?? DEFAULT_END_EVENTS;
            document.addEventListener('mousemove', handleMove);
            document.addEventListener('touchmove', handleTouchMove);
            activeEndEvents.forEach((type) => document.addEventListener(type, handleEnd));
        };

        const stop = () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('touchmove', handleTouchMove);
            activeEndEvents.forEach((type) => document.removeEventListener(type, handleEnd));
        };

        apiRef.current = { start, stop };
    }

    // Belt-and-suspenders: tear down listeners if the component unmounts mid-gesture.
    useEffect(() => apiRef.current!.stop, []);

    return apiRef.current;
}
